import { format, parse } from "date-fns"
import { Datepicker, Modal, ModalBody, ModalFooter, ModalHeader } from "flowbite-react"
import { useState } from "react"

interface ScheduleCallModalProps {
  isOpen: boolean
  onClose: () => void
  onSchedule: (scheduledAt: string) => void
  initialScheduledAt: string | null
}

const datepickerOptions = {
  root: {
    base: "relative"
  },
  popup: {
    root: {
      base: "absolute top-10 z-50 block pt-2",
      inline: "relative top-0 z-auto",
      inner: "inline-block rounded-lg bg-white p-4 shadow-lg dark:bg-gray-700"
    },
    header: {
      base: "",
      title: "px-2 py-3 text-center font-semibold text-gray-900 dark:text-white",
      selectors: {
        base: "mb-2 flex justify-between",
        button: {
          base: "rounded-lg bg-white px-5 py-2.5 text-sm font-semibold text-gray-900 hover:bg-gray-100 focus:outline-none focus:ring-2 focus:ring-gray-200 dark:bg-gray-700 dark:text-white dark:hover:bg-gray-600",
          prev: "",
          next: "",
          view: ""
        }
      }
    },
    view: {
      base: "p-1"
    },
    footer: {
      base: "mt-2 flex space-x-2",
      button: {
        base: "w-full rounded-lg px-5 py-2 text-center text-sm font-medium focus:ring-4 focus:ring-blue-200",
        today: "bg-blue-600 text-white hover:bg-blue-700 dark:bg-blue-500 dark:hover:bg-blue-600",
        clear:
          "border border-gray-300 bg-white text-gray-900 hover:bg-gray-100 dark:border-gray-600 dark:bg-gray-700 dark:text-white dark:hover:bg-gray-600"
      }
    }
  },
  views: {
    days: {
      header: {
        base: "mb-1 grid grid-cols-7",
        title: "h-6 text-center text-sm font-medium leading-6 text-gray-500 dark:text-gray-400"
      },
      items: {
        base: "grid w-64 grid-cols-7",
        item: {
          base: "block flex-1 cursor-pointer rounded-lg border-0 text-center text-sm font-semibold leading-9 text-gray-900 hover:bg-gray-100 dark:text-white dark:hover:bg-gray-600",
          selected: "bg-blue-600 text-white hover:bg-blue-500",
          disabled: "text-gray-500"
        }
      }
    },
    months: {
      items: {
        base: "grid w-64 grid-cols-4",
        item: {
          base: "block flex-1 cursor-pointer rounded-lg border-0 text-center text-sm font-semibold leading-9 text-gray-900 hover:bg-gray-100 dark:text-white dark:hover:bg-gray-600",
          selected: "bg-blue-600 text-white hover:bg-blue-500",
          disabled: "text-gray-500"
        }
      }
    },
    years: {
      items: {
        base: "grid w-64 grid-cols-4",
        item: {
          base: "block flex-1 cursor-pointer rounded-lg border-0 text-center text-sm font-semibold leading-9 text-gray-900 hover:bg-gray-100 dark:text-white dark:hover:bg-gray-600",
          selected: "bg-blue-600 text-white hover:bg-blue-500",
          disabled: "text-gray-500"
        }
      }
    },
    decades: {
      items: {
        base: "grid w-64 grid-cols-4",
        item: {
          base: "block flex-1 cursor-pointer rounded-lg border-0 text-center text-sm font-semibold leading-9 text-gray-900 hover:bg-gray-100 dark:text-white dark:hover:bg-gray-600",
          selected: "bg-blue-600 text-white hover:bg-blue-500",
          disabled: "text-gray-500"
        }
      }
    }
  }
}

export function ScheduleCallModal({ isOpen, onClose, onSchedule, initialScheduledAt }: ScheduleCallModalProps) {
  const currrentTime = new Date()
  // add an hour to the current time and drop the minutes and seconds
  currrentTime.setHours(currrentTime.getHours() + 1)
  currrentTime.setMinutes(0)
  currrentTime.setSeconds(0)

  const initialDate = initialScheduledAt ? new Date(initialScheduledAt) : new Date()
  const [selectedDate, setSelectedDate] = useState<Date>(initialDate)
  const [selectedTime, setSelectedTime] = useState<string>(
    initialScheduledAt ? format(initialDate, "HH:mm") : format(currrentTime, "HH:mm")
  )

  const handleSchedule = () => {
    const dateTime = parse(selectedTime, "HH:mm", selectedDate)
    const scheduledAt = format(dateTime, "yyyy-MM-dd'T'HH:mm:ssXXX")
    onSchedule(scheduledAt)
    onClose()
  }

  return (
    <Modal show={isOpen} onClose={onClose} size="md" className="pt-safe">
      <ModalHeader className="border-b border-gray-100">Schedule Call</ModalHeader>
      <ModalBody className="">
        <div className="flex flex-col items-start gap-4">
          <p className="text-sm text-gray-700">
            Please select a time and date for your call. The call will be placed at the scheduled time.
          </p>
          <div className="flex flex-col gap-1 w-full">
            <label htmlFor="time" className="block text-sm font-medium text-gray-700">
              Time
            </label>
            <input
              type="time"
              id="time"
              value={selectedTime}
              onChange={(e) => setSelectedTime(e.target.value)}
              className="px-4 py-3 mt-1 block w-full border border-gray-300 bg-white rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500 sm:text-base"
            />
          </div>
          <div className="self-center">
            <Datepicker
              inline
              onChange={(date: Date | null) => date && setSelectedDate(date)}
              theme={datepickerOptions}
              value={selectedDate}
            />
          </div>
        </div>
      </ModalBody>
      <ModalFooter>
        <button
          onClick={handleSchedule}
          className="w-full inline-flex justify-center rounded-md border border-transparent shadow-sm px-4 py-2 bg-blue-600 text-base font-medium text-white hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 "
        >
          Save
        </button>
        <button
          onClick={onClose}
          className="w-full inline-flex justify-center rounded-md border border-gray-300 shadow-sm px-4 py-2 bg-white text-base font-medium text-gray-700 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
        >
          Cancel
        </button>
      </ModalFooter>
    </Modal>
  )
}
